import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { ChevronLeft, ChevronRight, Download, Eye, Search, X } from "lucide-react";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import Spinner from "@/components/Spinner";
import { getTranferReferralHistory_Admin } from "@/api/Admin/transaction";
import type { TranferReferralHistoryAPI } from "@/api/Admin/type";
import { LIMIT_EXPORT_EXCEL } from "@/env";

type TransferStatus = "completed" | "pending" | "failed";

interface TransferHistoryItem {
  id: string;
  shortCode: string;
  customerId: number;
  customerName: string;
  packageName: string;
  fromStaff: string;
  toStaff: string;
  executedDate: string;
  executedBy: string;
  status: TransferStatus;
  commissionRate: string;
  paidMonths: number | null;
  remainingMonths: number;
  paidAmount: number;
  transferAmount: number;
  note: string;
}

type TransferHistoryApiItem = TranferReferralHistoryAPI["data"][number];

const ITEMS_PER_PAGE = 6;

const formatCurrency = (amount: number) => `${new Intl.NumberFormat("vi-VN").format(amount)} đ`;

const statusToneMap: Record<TransferStatus, string> = {
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const packageToneMap: Record<string, string> = {
  Titan: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  Pioneer: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
  Summit: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
};

const parseTransferDate = (dateValue: string | null | undefined) => {
  if (!dateValue) {
    return null;
  }

  const normalizedValue = dateValue.trim();
  if (!normalizedValue) {
    return null;
  }

  const [datePart, timePart] = normalizedValue.split(" ");
  const [day, month, year] = (datePart || "").split("/");

  if (!day || !month || !year) {
    const fallbackDate = new Date(normalizedValue);
    return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
  }

  const [hours = "00", minutes = "00", seconds = "00"] = (timePart || "").split(":");
  const parsedDate = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds),
  );

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const mapApiStatus = (status: string | null | undefined): TransferStatus => {
  const normalizedStatus = (status || "").toLowerCase();

  if (["completed", "complete", "success", "succeed", "done"].includes(normalizedStatus)) {
    return "completed";
  }

  if (["failed", "fail", "rejected", "error"].includes(normalizedStatus)) {
    return "failed";
  }

  return "pending";
};

const formatApiDate = (dateValue: string | null | undefined) => {
  if (!dateValue) {
    return "-";
  }

  const parsedDate = parseTransferDate(dateValue);
  return parsedDate ? dayjs(parsedDate).format("DD/MM/YYYY") : dateValue;
};

const formatApiTime = (dateValue: string | null | undefined) => {
  if (!dateValue) {
    return "--:--";
  }

  const parsedDate = parseTransferDate(dateValue);
  return parsedDate ? dayjs(parsedDate).format("HH:mm") : "--:--";
};

const formatCommissionRate = (rate: number | null | undefined) => {
  const numericRate = Number(rate ?? 0);
  return `${numericRate.toLocaleString("vi-VN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};

const mapTransferHistoryItem = (item: TransferHistoryApiItem): TransferHistoryItem => ({
  id: String(item.id),
  shortCode: `#T${String(item.id).padStart(3, "0")}`,
  customerId: item.customer_id,
  customerName: item.customer_name,
  packageName: item.package_name,
  fromStaff: item.old_referrer,
  toStaff: item.new_referrer,
  executedDate: item.transfer_date || "",
  executedBy: item.admin_name,
  status: mapApiStatus(item.status),
  commissionRate: formatCommissionRate(item.commission_rate),
  paidMonths: null,
  remainingMonths: item.remaining_months,
  paidAmount: Number(item.paid_to_old || 0),
  transferAmount: Number(item.paid_to_new || 0),
  note: item.note || "-",
});

const TransferHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [staffFilter, setStaffFilter] = useState("all");
  const [activeTab, setActiveTab] = useState<"all" | TransferStatus>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState<TransferHistoryItem | null>(null);
  const [transferHistoryData, setTransferHistoryData] = useState<TransferHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [paginationMeta, setPaginationMeta] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
  });

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/admin/dashboard" },
    { label: t("transferHistory.title") },
  ];

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, startDate, endDate]);

  useEffect(() => {
    const fetchTransferHistory = async () => {
      setLoading(true);

      try {
        const response = await getTranferReferralHistory_Admin(
          currentPage,
          ITEMS_PER_PAGE,
          debouncedSearchTerm || undefined,
          startDate || undefined,
          endDate || undefined,
        );

        const mappedItems: TransferHistoryItem[] = (response.data || []).map(mapTransferHistoryItem);

        setTransferHistoryData(mappedItems);
        setPaginationMeta({
          currentPage: response.meta?.current_page || currentPage,
          lastPage: response.meta?.last_page || 1,
          total: response.meta?.total || mappedItems.length,
        });
      } catch (error) {
        console.error("Failed to fetch transfer history:", error);
        setTransferHistoryData([]);
        setPaginationMeta({
          currentPage: 1,
          lastPage: 1,
          total: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    void fetchTransferHistory();
  }, [currentPage, debouncedSearchTerm, endDate, startDate]);

  const matchesLocalFilters = (item: TransferHistoryItem) => {
    const matchesTab = activeTab === "all" || item.status === activeTab;
    const matchesStaff =
      staffFilter === "all"
      || item.fromStaff === staffFilter
      || item.toStaff === staffFilter;

    return matchesTab && matchesStaff;
  };

  const filteredData = useMemo(() => {
    return transferHistoryData.filter(matchesLocalFilters);
  }, [activeTab, staffFilter, transferHistoryData]);

  const totalPages = Math.max(1, paginationMeta.lastPage);
  const paginationItems = useMemo(() => {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }, [totalPages]);

  const getStatusLabel = (status: TransferStatus) => {
    if (status === "completed") return t("transferHistory.statusCompleted");
    if (status === "pending") return t("transferHistory.statusPending");
    return t("transferHistory.statusFailed");
  };

  const handleExportExcel = async () => {
    setExporting(true);

    try {
      const firstPage = await getTranferReferralHistory_Admin(
        1,
        LIMIT_EXPORT_EXCEL,
        debouncedSearchTerm || undefined,
        startDate || undefined,
        endDate || undefined,
      );

      const allTransferRows: TransferHistoryApiItem[] = [...(firstPage.data || [])];
      const lastPage = firstPage.meta?.last_page || 1;

      if (lastPage > 1) {
        const pageRequests: Promise<TranferReferralHistoryAPI>[] = [];

        for (let pageIndex = 2; pageIndex <= lastPage; pageIndex += 1) {
          pageRequests.push(
            getTranferReferralHistory_Admin(
              pageIndex,
              LIMIT_EXPORT_EXCEL,
              debouncedSearchTerm || undefined,
              startDate || undefined,
              endDate || undefined,
            ),
          );
        }

        const pageResults = await Promise.all(pageRequests);
        pageResults.forEach((result) => {
          allTransferRows.push(...(result.data || []));
        });
      }

      const exportItems = allTransferRows
        .map(mapTransferHistoryItem)
        .filter(matchesLocalFilters);

      if (exportItems.length === 0) {
        message.info("Khong co du lieu de xuat Excel");
        return;
      }

      const columns = [
        "STT",
        "MA_CHUYEN_GIAO",
        "MA_NGAN",
        "KHACH_HANG",
        "ID_KHACH_HANG",
        "GOI_DAU_TU",
        "TU_NHAN_VIEN",
        "DEN_NHAN_VIEN",
        "NGAY_THUC_HIEN",
        "GIO_THUC_HIEN",
        "THUC_HIEN_BOI",
        "TRANG_THAI",
        "TY_LE_HOA_HONG",
        "THANG_CON_LAI",
        "DA_TRA_CHO_A",
        "CHUYEN_CHO_B",
        "GHI_CHU",
      ] as const;

      const headerMap: Record<(typeof columns)[number], string> = {
        STT: "STT",
        MA_CHUYEN_GIAO: t("transferHistory.tableTransactionCode"),
        MA_NGAN: "Short code",
        KHACH_HANG: t("transferHistory.tableCustomer"),
        ID_KHACH_HANG: t("transferHistory.customerId"),
        GOI_DAU_TU: t("transferHistory.tablePackage"),
        TU_NHAN_VIEN: t("transferHistory.staffA"),
        DEN_NHAN_VIEN: t("transferHistory.staffB"),
        NGAY_THUC_HIEN: t("transferHistory.tableExecutedDate"),
        GIO_THUC_HIEN: "Giờ thực hiện",
        THUC_HIEN_BOI: t("transferHistory.executedBy"),
        TRANG_THAI: t("transferHistory.tableStatus"),
        TY_LE_HOA_HONG: t("transferHistory.commissionRate"),
        THANG_CON_LAI: t("transferHistory.remainingMonths"),
        DA_TRA_CHO_A: t("transferHistory.totalPaidToA"),
        CHUYEN_CHO_B: t("transferHistory.transferredToB", { months: "" }).trim(),
        GHI_CHU: t("transferHistory.note"),
      };

      const exportRows = exportItems.map((item, index) => ({
        STT: index + 1,
        MA_CHUYEN_GIAO: item.id,
        MA_NGAN: item.shortCode,
        KHACH_HANG: item.customerName,
        ID_KHACH_HANG: item.customerId,
        GOI_DAU_TU: item.packageName,
        TU_NHAN_VIEN: item.fromStaff,
        DEN_NHAN_VIEN: item.toStaff,
        NGAY_THUC_HIEN: formatApiDate(item.executedDate),
        GIO_THUC_HIEN: formatApiTime(item.executedDate),
        THUC_HIEN_BOI: item.executedBy,
        TRANG_THAI: getStatusLabel(item.status),
        TY_LE_HOA_HONG: item.commissionRate,
        THANG_CON_LAI: item.remainingMonths,
        DA_TRA_CHO_A: item.paidAmount,
        CHUYEN_CHO_B: item.transferAmount,
        GHI_CHU: item.note,
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows, { header: columns as unknown as string[] });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transfer History");
      XLSX.utils.sheet_add_aoa(worksheet, [columns.map((column) => headerMap[column])], { origin: "A1" });

      const now = new Date();
      const fileName = `transfer_history_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success(`Xuat Excel thanh cong (${exportItems.length} ban ghi)`);
    } catch (error) {
      console.error("Export transfer history error:", error);
      message.error("Xuat Excel that bai, vui long thu lai");
    } finally {
      setExporting(false);
    }
  };

  const tableRangeStart = paginationMeta.total === 0 ? 0 : (paginationMeta.currentPage - 1) * ITEMS_PER_PAGE + 1;
  const tableRangeEnd = tableRangeStart === 0 ? 0 : tableRangeStart + filteredData.length - 1;

  return (
    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>{t("transferHistory.title")} - Admin Dashboard</title>
      </Helmet>

      <PageHeader
        title={t("transferHistory.title")}
        description={t("transferHistory.subtitle")}
        breadcrumbs={breadcrumbs}
      />
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 flex flex-col xl:flex-row xl:items-center gap-3 border-b border-gray-100 dark:border-gray-700">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t("transferHistory.searchPlaceholder")}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 pl-9 pr-3 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 xl:min-w-[320px]">
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              aria-label={t("transferHistory.startDate")}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              aria-label={t("transferHistory.endDate")}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <button
            type="button"
            onClick={handleExportExcel}
            disabled={loading || exporting}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 text-sm font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Đang Xuất File..." : t("transferHistory.export")}
          </button>
        </div>

        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
          {t("transferHistory.showingRecords", { count: filteredData.length })}
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <Spinner size={40} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] border-collapse">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  {[
                    t("transferHistory.tableTransactionCode"),
                    t("transferHistory.tableCustomer"),
                    t("transferHistory.tablePackage"),
                    t("transferHistory.tableTransferFlow"),
                    t("transferHistory.tableExecutedDate"),
                    t("transferHistory.tableStatus"),
                    t("transferHistory.tableActions"),
                  ].map((header) => (
                    <th
                      key={header}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors">
                    <td className="px-4 py-4 border-b border-gray-100 dark:border-gray-700 align-top">
                      <p className="text-sm font-semibold text-primary">{item.id}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{item.shortCode}</p>
                    </td>
                    <td className="px-4 py-4 border-b border-gray-100 dark:border-gray-700 align-top">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.customerName}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">ID: {item.customerId}</p>
                    </td>
                    <td className="px-4 py-4 border-b border-gray-100 dark:border-gray-700 align-top">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${packageToneMap[item.packageName] || "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"}`}>
                        {item.packageName}
                      </span>
                    </td>
                    <td className="px-4 py-4 border-b border-gray-100 dark:border-gray-700 align-top">
                      <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                        <span className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-xs font-medium">{item.fromStaff}</span>
                        <span className="text-gray-400">→</span>
                        <span className="px-2 py-1 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">{item.toStaff}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 border-b border-gray-100 dark:border-gray-700 align-top">
                      <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">{formatApiDate(item.executedDate)}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 whitespace-nowrap">
                        {item.executedBy}
                      </p>
                    </td>
                    <td className="px-4 py-4 border-b border-gray-100 dark:border-gray-700 align-top">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusToneMap[item.status]}`}>
                        {getStatusLabel(item.status)}
                      </span>
                    </td>
                    <td className="px-4 py-4 border-b border-gray-100 dark:border-gray-700 align-top">
                      <button
                        type="button"
                        onClick={() => setSelectedItem(item)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        {t("transferHistory.view")}
                      </button>
                    </td>
                  </tr>
                ))}

                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                      {t("transferHistory.noData")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-t border-gray-100 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
          <span>
            {t("transferHistory.paginationSummary", {
              from: tableRangeStart,
              to: tableRangeEnd,
              total: paginationMeta.total,
            })}
          </span>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1 || loading}
              className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {paginationItems.map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                disabled={loading}
                className={`w-8 h-8 inline-flex items-center justify-center rounded-md border text-sm ${
                  currentPage === page
                    ? "bg-primary border-primary text-white"
                    : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage === totalPages || loading}
              className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {selectedItem && (
        <div
          className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedItem(null);
            }
          }}
        >
          <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-800 shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t("transferHistory.detailTitle")}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{selectedItem.shortCode}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedItem(null)}
                className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 mb-3">{t("transferHistory.customerSection")}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-4">
                    <p className="text-xs text-gray-400 mb-1">{t("transferHistory.customerName")}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedItem.customerName}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-4">
                    <p className="text-xs text-gray-400 mb-1">{t("transferHistory.customerId")}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">#{selectedItem.customerId}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-4">
                    <p className="text-xs text-gray-400 mb-1">{t("transferHistory.packageLabel")}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedItem.packageName}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-4">
                    <p className="text-xs text-gray-400 mb-1">{t("transferHistory.transactionId")}</p>
                    <p className="text-sm font-semibold text-primary">{selectedItem.id}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 mb-3">{t("transferHistory.transferSection")}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-4">
                    <p className="text-xs text-gray-400 mb-1">{t("transferHistory.staffA")}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedItem.fromStaff}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-4">
                    <p className="text-xs text-gray-400 mb-1">{t("transferHistory.staffB")}</p>
                    <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">{selectedItem.toStaff}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-4">
                    <p className="text-xs text-gray-400 mb-1">{t("transferHistory.commissionRate")}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedItem.commissionRate}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-4">
                    <p className="text-xs text-gray-400 mb-1">{t("transferHistory.remainingMonths")}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedItem.remainingMonths} {t("transfer.months")}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-4">
                    <p className="text-xs text-gray-400 mb-1">{t("transferHistory.executedBy")}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{selectedItem.executedBy}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-4 border border-green-100 dark:border-green-900/40">
                    <p className="text-xs font-semibold text-green-700 dark:text-green-300 mb-1">
                      {selectedItem.paidMonths
                        ? t("transferHistory.paidToA", { months: selectedItem.paidMonths })
                        : t("transferHistory.totalPaidToA")}
                    </p>
                    <p className="text-xl font-bold text-green-700 dark:text-green-300">{formatCurrency(selectedItem.paidAmount)}</p>
                  </div>
                  <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 p-4 border border-amber-100 dark:border-amber-900/40">
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">
                      {t("transferHistory.transferredToB", { months: selectedItem.remainingMonths })}
                    </p>
                    <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{formatCurrency(selectedItem.transferAmount)}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-400 mb-3">{t("transferHistory.note")}</p>
                <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-4 text-sm text-gray-600 dark:text-gray-300">
                  {selectedItem.note}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setSelectedItem(null)}
                className="px-4 py-2 rounded-md border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t("transferHistory.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferHistoryPage;